import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { format } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useConfirm } from '../../components/common/ConfirmDialog';

const COVER_OUTCOMES = [
  'In Foal',
  'Not In Foal',
  'Pending'
];

const PREGNANCY_STATUSES = [
  'N/A',
  'Slipped',
  'Aborted',
  'Live Foal',
  'Stillborn',
];

const FERTILITY_STATUSES = [
  'Foaling Mare',
  'Barren',
  'Rested',
  'Pensioned',
];

const BREEDING_EVENT_TYPES = [
  { value: 'tease_score', label: 'Tease Score' },
  { value: 'palpation', label: 'Palpation' },
  { value: 'ovulation_confirmed', label: 'Ovulation Confirmed' },
  { value: 'pregnant', label: 'Pregnant' },
  { value: 'bred', label: 'Bred' },
];

const CoverRecordDialog = ({ open, onClose, onSuccess, horse, record, soldInfo, initialDate, showTypeSelector, onTypeChange, isFarm = false }) => {
  const [formData, setFormData] = useState({
    covering_sire: '',
    cover_date: '',
    outcome: '',
    is_current_cover: false,
    pregnancy_status: '',
    fertility_status: '',
    estimated_due_date: '',
  });
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    if (record) {
      setFormData({
        covering_sire: record.covering_sire || '',
        cover_date: record.cover_date ? record.cover_date.split('T')[0] : '',
        outcome: record.outcome || '',
        is_current_cover: record.is_current_cover || false,
        pregnancy_status: record.pregnancy_status || '',
        fertility_status: record.fertility_status || '',
        estimated_due_date: record.estimated_due_date || '',
      });
    } else {
      setFormData({
        covering_sire: '',
        cover_date: initialDate || '',
        outcome: '',
        is_current_cover: false,
        pregnancy_status: '',
        fertility_status: '',
        estimated_due_date: '',
      });
    }
  }, [record, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.covering_sire || !formData.cover_date || !formData.outcome) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        horse_id: horse.id,
        covering_sire: formData.covering_sire,
        cover_date: formData.cover_date || null,  // plain YYYY-MM-DD — no T12 append needed
        outcome: formData.outcome,
        is_current_cover: formData.is_current_cover,
        pregnancy_status: formData.pregnancy_status || null,
        fertility_status: formData.fertility_status || null,
        // estimated_due_date — YYYY-MM-DD plain date string (FarmFolio only, shown when In Foal)
        estimated_due_date: formData.estimated_due_date || null,
      };

      if (record) {
        await api.put(`/cover-records/${record.id}`, payload);
      } else {
        await api.post('/cover-records', payload);
      }
      
      // Reset form
      setFormData({
        covering_sire: '',
        cover_date: '',
        outcome: '',
        is_current_cover: false
      });
      
      onSuccess(payload);
    } catch (error) {
      console.error('Error saving cover record:', error);
      toast.error('Failed to save cover record');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!record?.id) return;
    if (!await confirm({ title: 'Delete this cover record?', destructive: true })) return;
    setSaving(true);
    try {
      await api.delete(`/cover-records/${record.id}`);
      toast.success('Cover record deleted');
      onSuccess({});
    } catch {
      toast.error('Failed to delete cover record');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{record ? 'Edit' : 'Add'} Cover</DialogTitle>
        </DialogHeader>
        {/* Item 3: Horse Sold badge */}
        {soldInfo && (
          <div className="bg-gray-100 border border-gray-300 rounded p-2 text-xs text-gray-700 font-medium">
            Horse Sold{soldInfo.sold_date ? ` on ${new Date(soldInfo.sold_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` : ''}. No further cover details available.
          </div>
        )}
        {/* Event type selector — shown only when opening from a calendar date click */}
        {showTypeSelector && !record && (
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground font-medium">Event Type</Label>
            <Select defaultValue="bred" onValueChange={v => {
              if (v !== 'bred' && onTypeChange) { onClose(); onTypeChange(v); }
            }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {BREEDING_EVENT_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Covering Sire */}
          <div className="space-y-2">
            <Label>Covering sire *</Label>
            <Input
              value={formData.covering_sire}
              onChange={(e) => setFormData({ ...formData, covering_sire: e.target.value })}
              placeholder="Enter sire name"
            />
          </div>

          {/* Cover Date */}
          <div className="space-y-2">
            <Label>Cover date *</Label>
            <Input
              type="date"
              value={formData.cover_date || ''}
              onChange={(e) => setFormData({ ...formData, cover_date: e.target.value })}
            />
          </div>

          {/* Outcome */}
          <div className="space-y-2">
            <Label>Outcome *</Label>
            <Select value={formData.outcome} onValueChange={(value) => setFormData({ ...formData, outcome: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select outcome" />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                {COVER_OUTCOMES.map(outcome => (
                  <SelectItem key={outcome} value={outcome}>{outcome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estimated Due Date — FarmFolio tenants only, shown when outcome is In Foal */}
          {isFarm && formData.outcome === 'In Foal' && (
            <div className="space-y-2">
              <Label>Estimated Due Date</Label>
              {/* Stored as plain YYYY-MM-DD per date-only field standard (dateUtils.js) */}
              <Input
                type="date"
                value={formData.estimated_due_date || ''}
                onChange={(e) => setFormData({ ...formData, estimated_due_date: e.target.value })}
                className="w-full sm:w-auto"
                data-testid="cover-estimated-due-date"
              />
            </div>
          )}

          {/* Pregnancy Status */}
          <div className="space-y-2">
            <Label>Pregnancy Status</Label>            <Select value={formData.pregnancy_status || '__none__'} onValueChange={(v) => setFormData({ ...formData, pregnancy_status: v === '__none__' ? '' : v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select pregnancy status" />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                <SelectItem value="__none__">— None —</SelectItem>
                {PREGNANCY_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fertility Status */}
          <div className="space-y-2">
            <Label>Fertility Status</Label>
            <Select value={formData.fertility_status || '__none__'} onValueChange={(v) => setFormData({ ...formData, fertility_status: v === '__none__' ? '' : v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select fertility status" />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                <SelectItem value="__none__">— None —</SelectItem>
                {FERTILITY_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Set as current cover status */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="current-cover"
              checked={formData.is_current_cover}
              onCheckedChange={(checked) => setFormData({ ...formData, is_current_cover: checked })}
            />
            <Label htmlFor="current-cover" className="text-sm font-normal cursor-pointer">
              Set as current cover status
            </Label>
          </div>

          <div className="flex items-center justify-between pt-2 border-t mt-4">
            <div>
              {record && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={handleDelete}
                  disabled={saving}
                  title="Delete cover record"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CoverRecordDialog;
